import {
  AdhocChannels,
  ConvChannels,
  type RActiveConversation,
  type RChunk,
  type RNewConversation,
  type RViewRecords,
} from 'v2/types/api/conversations'
import {
  type RAiMessageChunk,
  type RChatButtonChunk,
  type RChatMultiProductChunk,
  type RChatMenuButtonChunk,
  ChunkOrigin,
  type ChatMessagePayload,
  type RFileMessageChunk,
  type OriginFetchMessages,
  type InputChunks,
  type RChatMultiProductReplyChunk,
} from 'v2/types/chunk.external'
import { type ChunkMeta, ChunkType, type Chunk } from 'v2/types/chunk.internal'
import type { MessageEvent } from 'pubnub'

// CHUNK_STEP_2 - add a typeguard to be used in toChunkModel
// Update type guards to use the new types
// R stands for raw or external chunk that came from BE
export const isChatMessage = (
  m: MessageEvent['message'] | OriginFetchMessages['pbMessage']['message'],
): m is ChatMessagePayload => m.type === 'message' || m.type === 'chat'

export const isFileMessage = (
  m: MessageEvent['message'] | OriginFetchMessages['pbMessage']['message'],
): m is RFileMessageChunk => m.type === 'file'

export const isSignalMessage = (
  m: MessageEvent['message'] | OriginFetchMessages['pbMessage']['message'],
): m is {
  type: 'signal'
  content: ChunkMeta[ChunkType.signal] & { event_name: string; bot_uuid: string }
} => m.type === 'signal'

export const isRAiMessageChunk = (
  m: MessageEvent['message'] | OriginFetchMessages['pbMessage']['message'],
): m is RAiMessageChunk => m.type === 'aichat'

export const isRChatButtonChunk = (
  m: MessageEvent['message'] | OriginFetchMessages['pbMessage']['message'],
): m is RChatButtonChunk => m.type === 'chat_button'

export const isRChatMultiProductChunk = (
  m: MessageEvent['message'] | OriginFetchMessages['pbMessage']['message'],
): m is RChatMultiProductChunk => m.type === 'chat_multi_product'

export const isRChatMultiProductReplyChunk = (
  m: MessageEvent['message'] | OriginFetchMessages['pbMessage']['message'],
): m is RChatMultiProductReplyChunk => m.type === 'multi_product_reply'

export const isRChatMenuButtonChunk = (
  m: MessageEvent['message'] | OriginFetchMessages['pbMessage']['message'],
): m is RChatMenuButtonChunk => m.type === 'chat_menu_button'

// Chunk typeguards
// CHUNK_STEP_4 - add a typeguard for the chunktype for the UI
export const isChatMessageChunk = (m: Chunk): m is Chunk<ChunkType.chatMessage> =>
  m.type === ChunkType.chatMessage

export const isUnknownChunk = (m: Chunk): m is Chunk<ChunkType.unknown> =>
  m.type === ChunkType.unknown

export const isInternalChatChunk = (m: Chunk): m is Chunk<ChunkType.internal_chat> =>
  m.type === ChunkType.internal_chat

export const isNotesChunk = (m: Chunk): m is Chunk<ChunkType.notes> => m.type === ChunkType.notes

export const isRecordingChunk = (m: Chunk): m is Chunk<ChunkType.recording> =>
  m.type === ChunkType.recording

export const isVoiceMailChunk = (m: Chunk): m is Chunk<ChunkType.voicemail> =>
  m.type === ChunkType.voicemail

export function isFileChunk(chunk: Chunk<ChunkType>): chunk is Chunk<ChunkType.file> {
  return chunk.type === ChunkType.file
}

export function isTextChunk(chunk: Chunk<ChunkType>): chunk is Chunk<ChunkType.text> {
  return chunk.type === ChunkType.text
}

export function isAiChunk(chunk: Chunk<ChunkType>): chunk is Chunk<ChunkType.aiMessage> {
  return chunk.type === ChunkType.aiMessage
}

export function isSignalChunk(chunk: Chunk<ChunkType>): chunk is Chunk<ChunkType.signal> {
  return chunk.type === ChunkType.signal
}

export function isChatButtonChunk(chunk: Chunk<ChunkType>): chunk is Chunk<ChunkType.chatButton> {
  return chunk.type === ChunkType.chatButton
}

export function isDispositionChunk(chunk: Chunk<ChunkType>): chunk is Chunk<ChunkType.disposition> {
  return chunk.type === ChunkType.disposition
}

export function isTemplateChunk(chunk: Chunk<ChunkType>): chunk is Chunk<ChunkType.template> {
  return chunk.type === ChunkType.template
}

export function isButtonChunk(chunk: Chunk<ChunkType>): chunk is Chunk<ChunkType.button> {
  return chunk.type === ChunkType.button
}

export function isButtonReplyChunk(
  chunk: Chunk<ChunkType>,
): chunk is Chunk<ChunkType.button_reply> {
  return chunk.type === ChunkType.button_reply
}

export function isTaskChunk(chunk: Chunk<ChunkType>): chunk is Chunk<ChunkType.task> {
  return chunk.type === ChunkType.task
}

export function isChatMultiProductChunk(
  chunk: Chunk<ChunkType>,
): chunk is Chunk<ChunkType.chatMultiProduct> {
  return chunk.type === ChunkType.chatMultiProduct
}

export function isChatMenuButtonChunk(
  chunk: Chunk<ChunkType>,
): chunk is Chunk<ChunkType.chatMenuButton> {
  return chunk.type === ChunkType.chatMenuButton
}

export const isFlowExecutionChunk = (chunk: Chunk): chunk is Chunk<ChunkType.flowExecution> =>
  chunk.type === ChunkType.flowExecution

export function isListChunk(chunk: Chunk<ChunkType>): chunk is Chunk<ChunkType.list> {
  return chunk.type === ChunkType.list
}

export function isListReplyChunk(chunk: Chunk<ChunkType>): chunk is Chunk<ChunkType.list_reply> {
  return chunk.type === ChunkType.list_reply
}

export function isChatMultiProductReplyChunk(
  chunk: Chunk<ChunkType>,
): chunk is Chunk<ChunkType.multiProductReply> {
  return chunk.type === ChunkType.multiProductReply
}

export function isSlackChunk(chunk: Chunk<ChunkType>): chunk is Chunk<ChunkType.text> {
  return chunk.type === ChunkType.text && (chunk.channel as string) === ConvChannels.SLACK
}

const isInputChunkType = (m: InputChunks, origin: ChunkOrigin, type: string): boolean =>
  m && origin === type

// Typeguards for origin
export const fromPbMessage = (m: InputChunks, origin: ChunkOrigin): m is MessageEvent =>
  isInputChunkType(m, origin, ChunkOrigin.pubnubChatEvent)
export const fromFetchMessages = (m: InputChunks, origin: ChunkOrigin): m is OriginFetchMessages =>
  isInputChunkType(m, origin, ChunkOrigin.pubnubFetchMessages)
export const fromChunkApi = (m: InputChunks, origin: ChunkOrigin): m is RChunk =>
  isInputChunkType(m, origin, ChunkOrigin.apiFetchChunks)
export const isOriginFetchMessages = (
  msg: MessageEvent | OriginFetchMessages,
): msg is OriginFetchMessages => 'pbMessage' in msg

export const fromAddNotes = (m: InputChunks, origin: ChunkOrigin): m is RChunk =>
  isInputChunkType(m, origin, ChunkOrigin.addNotes) // Conversation type guards

// Conversation Type Guards
export const isActiveConversation = (conversation: unknown): conversation is RActiveConversation =>
  typeof conversation === 'object' &&
  conversation !== null &&
  'conversation_number' in conversation &&
  'contact_identifier' in conversation &&
  'parent_channel' in conversation &&
  'source_direction_inbound' in conversation

export const isFlowTask = (conversation: unknown): conversation is RActiveConversation =>
  typeof conversation === 'object' &&
  conversation !== null &&
  'channel' in conversation &&
  'child_conversations' in conversation &&
  Array.isArray(conversation.child_conversations) &&
  conversation.child_conversations.length === 1 &&
  [AdhocChannels.TASK, AdhocChannels.CALLBACK].includes(conversation.child_conversations[0].channel)

export const isViewRecord = (record: unknown): record is RViewRecords['records'][0] =>
  typeof record === 'object' &&
  record !== null &&
  'conv_number' in record &&
  'contact_identifier' in record &&
  'parent_channel' in record &&
  'direction_inbound' in record

export const isNewConversation = (
  conversation: RActiveConversation | RNewConversation | RViewRecords['records'][0],
): conversation is RNewConversation => conversation && 'invite_type' in conversation
